import React from 'react';

import Modal from 'antd/es/modal/Modal';
import { Field } from 'formik';
import * as Yup from 'yup';

import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { FileInterface, FileType } from '@common/typescript/objects/FileInterface';
import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import VetTesPreviewModal from '@app/components/Pages/VetTestimonials/VetTesPreviewModal';
import { pushEvent } from '@app/components/TagManager';
import AttachmentPreview from '@app/components/UI/Attachment/AttachmentPreview';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import CustomReCAPTCHA from '@app/components/UI/CustomReCAPTCHA/CustomReCAPTCHA';
import CustomAutoComplete from '@app/components/UI/Forms/CustomAutoComplete/CustomAutoComplete';
import CustomCheckbox from '@app/components/UI/Forms/CustomCheckbox/CustomCheckbox';
import FormikField from '@app/components/UI/Forms/CustomFormikField/CustomFormikField';
import CustomSelect from '@app/components/UI/Forms/CustomSelect/CustomSelect';
import FileUpload from '@app/components/UI/Forms/FileUpload/FileUpload';
import { ListOption } from '@app/objects/SidebarProps';
import { StatesListItem } from '@app/objects/StatesListItem';
import { VetTestimonialsRes } from '@app/objects/VetTestimonialsRes';
import { Veterinarian } from '@app/objects/Veterinarian';
import { validateRequiredString } from '@app/utils/Validators';
import { formatListWithOr } from '@app/utils/formatListWithOr';
import { onFileUpload } from '@app/utils/onFileUpload';
import { transformStateList, transformVetList } from '@app/utils/transformList';

import '@app/scss/components/vetTesModal.scss';
import '@app/scss/localCommon/components/attachment.scss';

interface VetTesModalProps {
	modalVisible: boolean;
	setModalVisible: (e: boolean) => void;
	sent: boolean;
	setSent: (e: boolean) => void;
}

const initialValues = {
	id: -1,
	firstName: '',
	lastName: '',
	isDisplayed: true,
	city: '',
	state: '',
	email: '',
	vetId: '',
	message: '',
	filePath: [],
	captcha: '',
};

const validationSchema = Yup.object({
	firstName: validateRequiredString('First Name is a required field'),
	city: validateRequiredString('City is a required field'),
	state: validateRequiredString('State is a required field'),
	vetId: Yup.string().nullable('Veterinarian is a required field').required('Veterinarian is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	captcha: Yup.string().required('Please check the box to submit this form'),
	message: Yup.string().nullable().required('Tell us about your experience is a required field'),
});

const VetTesModal: React.FC<VetTesModalProps> = ({
	modalVisible,
	setModalVisible,
	sent,
	setSent,
}) => {
	const [isChecked, setIsChecked] = React.useState(true);
	const [attachment, setAttachment] = React.useState<Array<FileInterface>>([]);
	const maxFile = 10000000;
	const imgExtension = ['jpg', 'jpeg', 'jfif', 'png'];
	const maxImgCount = 1;
	const [stateList, setStateList] = React.useState<Array<StatesListItem>>([]);
	const stateListOptions: Array<ListOption> = [];
	const vetListOptions: Array<ListOption> = [];
	const [vetIdList, setVetIdList] = React.useState<Array<Veterinarian>>([]);
	const [modalPreviewVisible, setModalPreviewVisible] = React.useState<boolean>(false);
	const [formValues, setFormValues] = React.useState({});
	const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);

	const context = useItemProviderContext<VetTestimonialsRes>();

	if (!context?.state) throw new Error('Error context');

	const { state: { loading } } = context;

	const openModal = (e, values) => {
		e.preventDefault();
		setFormValues((prevState) => ({
			...prevState,
			...values,
		}));
		setModalPreviewVisible(true);
	};

	const getVeterinarians = (name: string) => {
		rest.get<List<Veterinarian>>(`v1/veterinarians/?count=300&name=${name}`)
			.then((res) => {
				const vetIdListSorted = res.list.sort((a, b) => (a.firstName > b.firstName ? 1 : -1));
				setVetIdList(vetIdListSorted);
			});
	};

	React.useEffect(() => {
		if (attachment[0]) {
			setFormValues((prevState) => ({
				...prevState,
				imgSrc: attachment[0].src,
			}));
		}
	}, [attachment]);

	React.useEffect(() => {
		setFormValues((prevState) => ({
			...prevState,
			isShowName: isChecked,
		}));
	}, [isChecked]);

	React.useEffect(() => {
		rest.get<List<StatesListItem>>('v1/states')
			.then((res) => setStateList(res.list));
	}, []);

	transformStateList(stateList, stateListOptions);
	transformVetList(vetIdList, vetListOptions);

	return (
		<>
			<Modal
				wrapClassName="modal-wrap vet-tes-modal"
				open={modalVisible}
				onOk={() => setModalVisible(false)}
				onCancel={() => setModalVisible(false)}
				width={1037}
				centered
			>
				<div className="vet-tes-modal__content">
					<div className="modal-wrap__container">
						{!sent ? (
							<>
								<h3 className="vet-tes-modal-title">Submit a testimonial</h3>
								<h4 className="vet-tes-modal-subtitle vet-tes-modal-subtitle_desktop">
									Write a testimonial to the loving and compassionate veterinarian who helped your pet peacefully cross the Rainbow
									Bridge.
								</h4>
							</>
						) : (
							<>
								<h3 className="vet-tes-modal-title vet-tes-modal-title_success">Thank you for submitting your testimonial</h3>
								<h4 className="vet-tes-modal-subtitle">
									We will post it within 5 business days and you will receive an email letting you know the testimonial for your Lap
									of Love veterinarian is ready to view.
								</h4>
							</>
						)}
						{!sent && (
							<>
								<h4 className="vet-tes-modal__form-title">Personal information</h4>
								<div className="vet-tes-modal__form">
									<ItemEditor
										formikProps={{
											initialValues,
											validationSchema,
										}}
										readonly={false}
										withButtons={false}
										beforeSubmit={(values, action, submit) => {
											if (isConfirmed) {
												values.vetId = vetListOptions.find((option) => option.value === values.vetId)?.id || null;

												if (typeof values.vetId === 'number') {
													submit();
												}
											}
										}}
										afterSubmit={(item) => {
											setSent(true);
											setAttachment([]);
											pushEvent('form_submit', { formName: 'Testimonial-Form' });
										}}
										customButtons={(item, formikBag) => (
											<div className="modal-button">
												<button type="submit" className="btn btn-primary" disabled={loading}>
													Submit your testimonial
												</button>
												{loading && <BounceLoader />}
												<button type="button" className="btn btn-primary" onClick={(e) => openModal(e, formikBag.values)}>
													Preview your testimonial
												</button>
											</div>
										)}
										edit={(formikBag) => (
											<>
												<CustomInput
													type="text"
													name="firstName"
													placeholder="First Name*"
													formikBag={formikBag}
												/>
												<CustomInput
													type="text"
													name="lastName"
													placeholder="Last Name"
													formikBag={formikBag}
												/>
												<CustomCheckbox
													isChecked={isChecked}
													setIsChecked={setIsChecked}
													formikBag={formikBag}
													capture="Would you like your name displayed with your testimonial?"
												/>
												<CustomInput
													type="text"
													name="city"
													placeholder="City*"
													formikBag={formikBag}
												/>
												<CustomSelect
													name="state"
													placeholder="State*"
													onSelect={(value, option) => {
														formikBag.setFieldValue('state', value);
													}}
													onChange={(value, option) => {
														formikBag.setFieldValue('state', value);
													}}
													options={stateListOptions}
													showSearch
													onFocus={() => {}}
													formikBag={formikBag}
												/>
												{!formikBag.values.state ? <div className="break" /> : null}
												<CustomInput
													name="email"
													placeholder="Email Address*"
													formikBag={formikBag}
													trimValue
												/>
												<FormikField
													fieldName="vetId"
													containerClassName="custom-field custom-field__search"
													render={(field) => (
														<CustomAutoComplete
															field={field}
															placeholder="Select or type Veterinarian Name*"
															onChange={(value) => {
																formikBag.setFieldValue('vetId', value);
																getVeterinarians(value);
															}}
															onBlur={() => formikBag.setFieldTouched('vetId', true)}
															options={vetListOptions}
															adjustValue
														/>
													)}
												/>
												<div className="vet-tes-modal__form-textarea">
													<CustomInput
														component="textarea"
														name="message"
														placeholder="Tell us about your experience*"
														formikBag={formikBag}
													/>
												</div>
												<Field
													name="filePath"
												>
													{(field) => {
														return (
															<div
																className={
																	field.form.errors.filePath && field.form.touched.filePath
																		? 'attachment attachment_cropped'
																		: 'attachment'
																}
															>
																<h4 className="attachment-title">Upload picture of your pet</h4>
																<AttachmentPreview
																	attachment={attachment}
																	setAttachment={setAttachment}
																	formikBag={formikBag}
																/>
																<div className="attachment-upload-container">
																	<FileUpload
																		update={(data) => onFileUpload(
																			data,
																			field,
																			formikBag,
																			'filePath',
																			maxFile,
																			imgExtension,
																			attachment,
																			maxImgCount,
																			setAttachment,
																		)}
																		type="jobInquiry"
																		fileType={FileType.File}
																		noRelation
																		asButton
																		buttonClassName="btn btn-secondary"
																		buttonCaption="Upload a file"
																		multiple={false}
																		aspectRatio={16 / 9}
																		maxFile={maxFile}
																		field={field}
																		fileNote=""
																		imgExtensions={imgExtension}
																	/>
																	<span className="attachment-note">
																		(Maximum file size is 10MB in
																		{` ${formatListWithOr(imgExtension)} `}
																		format)
																	</span>
																</div>
																{field.form.errors.filePath && field.form.touched.filePath ? (
																	<div className="custom-input__error error_top-gap">
																		{' '}
																		{field.form.errors.filePath}
																	</div>
																) : null}
															</div>
														);
													}}
												</Field>
												<FormikField
													containerClassName="captcha"
													fieldName="captcha"
													render={(fieldProps) => (
														<CustomReCAPTCHA
															formikBag={formikBag}
															onChange={(value) => {
																if (value) {
																	formikBag.setFieldValue('captcha', value);
																	setIsConfirmed(true);
																} else {
																	formikBag.setFieldValue('captcha', '');
																	setIsConfirmed(false);
																}
															}}
														/>
													)}
												/>
											</>
										)}
									/>
								</div>
							</>
						)}
					</div>
				</div>
			</Modal>
			<VetTesPreviewModal modalVisible={modalPreviewVisible} setModalVisible={setModalPreviewVisible} tes={formValues} />
		</>
	);
};

export default VetTesModal;
